/**
 * ChatMessage — one row inside the support widget conversation.
 *
 * User and assistant messages differ in alignment, background,
 * and metadata slot. Assistant messages while `streaming` render a
 * subtle skeleton shimmer bar under the text so the operator can see
 * more tokens are on the way (per feedback_ui_utility_and_class:
 * never utilitarian-only).
 *
 * Tool-call and ticket events attached by the hook render inline as
 * chips beneath the assistant text — the caller doesn't have to know
 * a tool ran, but power users watching the widget can see the mechanism.
 */

import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Box,
  Chip,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import { ROLE_ASSISTANT, ROLE_USER } from "./constants";

const bubbleSx = (role, theme) => {
  const isUser = role === ROLE_USER;
  const isDark = theme.palette.mode === "dark";
  return {
    px: 1.5,
    py: 1,
    borderRadius: 2,
    maxWidth: "85%",
    wordBreak: "break-word",
    // User bubble stays plain-text; whitespace preserved so a
    // multi-line paste renders as typed. Assistant bubble uses
    // ReactMarkdown, which produces its own block layout — pre-wrap
    // there would double-space paragraphs.
    whiteSpace: isUser ? "pre-wrap" : "normal",
    fontSize: "0.9rem",
    lineHeight: 1.45,
    ...(isUser
      ? {
          bgcolor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          borderTopRightRadius: 4,
        }
      : {
          bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
          color: theme.palette.text.primary,
          borderTopLeftRadius: 4,
        }),
  };
};

// Scoped renderers so assistant markdown fits inside a chat bubble —
// headings capped, list padding tight, code inline vs block styled,
// links open in a new tab with rel="noopener noreferrer" (LLM output
// is UNTRUSTED, so we never allow raw HTML and never trust a URL).
const MD_COMPONENTS = {
  h1: ({ node, ...p }) => (
    <Typography component="h1" sx={{ fontWeight: 600, fontSize: "1.05rem", mt: 1, mb: 0.5 }} {...p} />
  ),
  h2: ({ node, ...p }) => (
    <Typography component="h2" sx={{ fontWeight: 600, fontSize: "1rem", mt: 1, mb: 0.5 }} {...p} />
  ),
  h3: ({ node, ...p }) => (
    <Typography component="h3" sx={{ fontWeight: 600, fontSize: "0.95rem", mt: 0.75, mb: 0.25 }} {...p} />
  ),
  h4: ({ node, ...p }) => (
    <Typography component="h4" sx={{ fontWeight: 600, fontSize: "0.9rem", mt: 0.5, mb: 0.25 }} {...p} />
  ),
  p: ({ node, ...p }) => (
    <Typography component="p" sx={{ fontSize: "0.9rem", lineHeight: 1.45, my: 0.5 }} {...p} />
  ),
  a: ({ node, ...p }) => (
    <a
      {...p}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: "inherit", textDecoration: "underline" }}
    />
  ),
  ul: ({ node, ordered, ...p }) => (
    <Box component="ul" sx={{ my: 0.5, pl: 2.5 }} {...p} />
  ),
  ol: ({ node, ordered, ...p }) => (
    <Box component="ol" sx={{ my: 0.5, pl: 2.5 }} {...p} />
  ),
  li: ({ node, ordered, checked, ...p }) => (
    <Box component="li" sx={{ my: 0.25, fontSize: "0.9rem" }} {...p} />
  ),
  code: ({ node, inline, className, children, ...p }) =>
    inline ? (
      <Box
        component="code"
        sx={{
          px: 0.5,
          py: "1px",
          borderRadius: 0.5,
          bgcolor: "action.hover",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: "0.85em",
        }}
        {...p}
      >
        {children}
      </Box>
    ) : (
      <Box
        component="code"
        sx={{
          display: "block",
          p: 1,
          borderRadius: 1,
          bgcolor: "action.hover",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: "0.85em",
          overflowX: "auto",
          whiteSpace: "pre",
        }}
        {...p}
      >
        {children}
      </Box>
    ),
  pre: ({ node, ...p }) => <Box component="pre" sx={{ my: 0.5, overflowX: "auto" }} {...p} />,
  hr: () => <Box sx={{ borderTop: 1, borderColor: "divider", my: 1, opacity: 0.5 }} />,
  blockquote: ({ node, ...p }) => (
    <Box
      component="blockquote"
      sx={{
        borderLeft: 3,
        borderColor: "divider",
        pl: 1,
        ml: 0,
        my: 0.5,
        opacity: 0.85,
      }}
      {...p}
    />
  ),
  table: ({ node, ...p }) => (
    <Box sx={{ overflowX: "auto", my: 0.5 }}>
      <Box component="table" sx={{ borderCollapse: "collapse", fontSize: "0.85rem" }} {...p} />
    </Box>
  ),
  th: ({ node, ...p }) => (
    <Box component="th" sx={{ border: 1, borderColor: "divider", px: 0.75, py: 0.25, fontWeight: 600, textAlign: "left" }} {...p} />
  ),
  td: ({ node, ...p }) => (
    <Box component="td" sx={{ border: 1, borderColor: "divider", px: 0.75, py: 0.25 }} {...p} />
  ),
};

export default function ChatMessage({ message }) {
  const { role, content, streaming, events, error } = message;
  const isUser = role === ROLE_USER;
  const align = isUser ? "flex-end" : "flex-start";

  return (
    <Stack
      direction="column"
      alignItems={align}
      spacing={0.5}
      sx={{ width: "100%" }}
      data-testid={`support-message-${role}`}
    >
      <Box sx={(theme) => bubbleSx(role, theme)}>
        {content ? (
          isUser ? (
            content
          ) : (
            // Assistant content = LLM output → parse as Markdown so
            // headings/lists/code/bold render properly instead of
            // showing literal `#`, `**`, `-`. ReactMarkdown sanitizes
            // by default: no raw HTML, no inline handlers, no
            // javascript: URLs — safe even if the model tries to
            // inject them.
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
              {content}
            </ReactMarkdown>
          )
        ) : (
          streaming && (
            // First-token latency placeholder — before any chunk arrives,
            // show a two-line skeleton so the bubble doesn't jump size
            // when the stream starts.
            <Box sx={{ minWidth: 160 }}>
              {/* Let MUI derive Skeleton bg from theme.palette.action.hover
                  so the shimmer stays visible in both light AND dark mode.
                  Hardcoding rgba(0,0,0,0.08) here made the placeholder
                  disappear against the dark-mode bubble (rgba(255,255,255,0.06)). */}
              <Skeleton variant="text" width="100%" />
              <Skeleton variant="text" width="60%" />
            </Box>
          )
        )}
      </Box>

      {/* Tool-call / ticket chips (assistant only) */}
      {role === ROLE_ASSISTANT && events && events.length > 0 && (
        <Stack
          direction="row"
          spacing={0.5}
          useFlexGap
          flexWrap="wrap"
          sx={{ maxWidth: "85%" }}
        >
          {events.map((ev, idx) => {
            if (ev.kind === "tool") {
              return (
                <Chip
                  key={`tool-${idx}`}
                  size="small"
                  variant="outlined"
                  icon={<BuildOutlinedIcon />}
                  label={ev.name}
                  sx={{ fontSize: "0.7rem", height: 22 }}
                />
              );
            }
            if (ev.kind === "ticket") {
              return (
                <Chip
                  key={`ticket-${idx}`}
                  size="small"
                  color="primary"
                  icon={<ConfirmationNumberOutlinedIcon />}
                  label={
                    ev.sla ? `${ev.id} · ${ev.sla}` : ev.id
                  }
                  sx={{ fontSize: "0.7rem", height: 22 }}
                />
              );
            }
            return null;
          })}
        </Stack>
      )}

      {/* Streaming shimmer bar (assistant only, only while streaming
          AND content is non-empty — pre-content case shows the
          two-line skeleton above instead). */}
      {role === ROLE_ASSISTANT && streaming && content && (
        <LinearProgress
          sx={{
            width: "40%",
            height: 2,
            borderRadius: 1,
            opacity: 0.5,
          }}
        />
      )}

      {/* Error surface — inline, red-accented, no toast noise */}
      {role === ROLE_ASSISTANT && error && (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ color: "error.main", pl: 0.5 }}
        >
          <ErrorOutlineIcon fontSize="inherit" />
          <Typography variant="caption">{error}</Typography>
        </Stack>
      )}
    </Stack>
  );
}
